import { useCallback, useEffect, useRef, useState } from "react";
import { useTourBlocker } from "../hooks/useTourBlocker";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../common/state/hooks";
import {
  getShowStandardModal,
  openStandardModal,
} from "../../../common/state/slice/modal/modalSlice";
import { getCurrentUser } from "../../profile/state/profileSlice";
import { enqueueOnboardingEvent } from "../events/eventQueue";
import {
  fetchOnboardingState,
  findResumableTours,
  selectActiveTour,
  selectOnboardingAudience,
  selectOnboardingRemote,
  selectSuggestionsMuted,
  selectOnboardingStatus,
  selectPendingTour,
} from "../state/onboardingSlice";
import { ONBOARDING_WELCOME_MODAL_ID, WelcomeModal } from "./WelcomeModal";
import { ONBOARDING_RELAUNCH_MODAL_ID, RelaunchPrompt } from "./RelaunchPrompt";
import { ChecklistLauncher } from "../components/ChecklistLauncher";
import { DemoCleanupPrompt } from "../components/DemoCleanupPrompt";
import { SpotlightOverlay } from "./SpotlightOverlay";
import { TourPopover } from "./TourPopover";
import { useTourRunner } from "./useTourRunner";
import { HELP_LAUNCHER_SELECTOR } from "../hooks/useHelpLauncherBox";

export const TOUR_ACTIVE_BODY_CLASS = "onboarding-tour-active";

const TOUR_CARD_SELECTOR = "[data-testid='onboarding-tour-card']";
const RESUME_PILL_BUTTON_SELECTOR = "[data-testid='onboarding-resume-pill'] button";
const LAUNCHER_SLOT_MARGIN_PX = 24;
const RESUME_PILL_HALF_WIDTH_PX = 90;
const RESUME_PILL_HALF_HEIGHT_PX = 20;
const FOCUS_RETRY_FRAMES = 30;

const getLauncherSlotCenter = (): { x: number; y: number } => {
  const helpLauncher = document.querySelector<HTMLElement>(HELP_LAUNCHER_SELECTOR);
  const rect = helpLauncher?.getBoundingClientRect();
  if (rect && rect.width > 0) {
    return {
      x: rect.left - LAUNCHER_SLOT_MARGIN_PX / 2 - RESUME_PILL_HALF_WIDTH_PX,
      y: rect.top + rect.height / 2,
    };
  }
  return {
    x: window.innerWidth - LAUNCHER_SLOT_MARGIN_PX - RESUME_PILL_HALF_WIDTH_PX,
    y: window.innerHeight - LAUNCHER_SLOT_MARGIN_PX - RESUME_PILL_HALF_HEIGHT_PX,
  };
};

const focusResumePill = (framesLeft = FOCUS_RETRY_FRAMES): void => {
  requestAnimationFrame(() => {
    const button = document.querySelector<HTMLElement>(RESUME_PILL_BUTTON_SELECTOR);
    if (button) {
      button.focus();
      return;
    }
    if (framesLeft > 0) {
      focusResumePill(framesLeft - 1);
    }
  });
};

export const TourLayer = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const currentUser = useAppSelector(getCurrentUser);
  const status = useAppSelector(selectOnboardingStatus);
  const audience = useAppSelector(selectOnboardingAudience);
  const remote = useAppSelector(selectOnboardingRemote);
  const suggestionsMuted = useAppSelector(selectSuggestionsMuted);
  const activeTour = useAppSelector(selectActiveTour);
  const pendingTour = useAppSelector(selectPendingTour);
  const showStandardModal = useAppSelector(getShowStandardModal);
  const { tour, step, stepIndex, targetEl, prefersReducedMotion, next, back, dismiss, handoff } =
    useTourRunner();
  const blockers = useTourBlocker();
  const hiddenByBlocker =
    blockers.length > 0 &&
    targetEl !== null &&
    !blockers.some((blocker) => blocker.contains(targetEl));
  const welcomeTriggeredRef = useRef(false);
  const relaunchTriggeredRef = useRef(false);
  const [referenceHidden, setReferenceHidden] = useState(false);
  const handleReferenceHiddenChange = useCallback((hidden: boolean) => {
    setReferenceHidden(hidden);
  }, []);
  const [focusWithinTarget, setFocusWithinTarget] = useState(false);

  useEffect(() => {
    if (!targetEl || !step?.hint) {
      setFocusWithinTarget(false);
      return undefined;
    }
    const update = () => setFocusWithinTarget(targetEl.contains(document.activeElement));
    update();
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", update);
    return () => {
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", update);
    };
  }, [targetEl, step]);

  const onboardingModalOpen =
    showStandardModal.show &&
    (showStandardModal.id === ONBOARDING_WELCOME_MODAL_ID ||
      showStandardModal.id === ONBOARDING_RELAUNCH_MODAL_ID);
  const resumable =
    activeTour || pendingTour || onboardingModalOpen
      ? undefined
      : findResumableTours(remote, audience, Date.now())[0];

  const dismissingRef = useRef(false);
  const handleDismiss = useCallback(() => {
    if (dismissingRef.current) {
      return;
    }
    const finish = () => {
      dismissingRef.current = false;
      dismiss();
      focusResumePill();
    };
    const card = document.querySelector<HTMLElement>(TOUR_CARD_SELECTOR);
    if (!card || typeof card.animate !== "function") {
      finish();
      return;
    }
    dismissingRef.current = true;
    const from = card.getBoundingClientRect();
    const to = getLauncherSlotCenter();
    const dx = to.x - (from.left + from.width / 2);
    const dy = to.y - (from.top + from.height / 2);
    const keyframes = prefersReducedMotion
      ? [{ opacity: 1 }, { opacity: 0 }]
      : [
          { transform: "translate(0, 0) scale(1)", opacity: 1 },
          { transform: `translate(${dx}px, ${dy}px) scale(0.15)`, opacity: 0 },
        ];
    card
      .animate(keyframes, {
        duration: prefersReducedMotion ? 120 : 280,
        easing: "cubic-bezier(0.4, 0, 0.2, 1)",
        fill: "forwards",
      })
      .finished.then(finish, finish);
  }, [dismiss, prefersReducedMotion]);

  const tourIsActive = Boolean(activeTour);

  useEffect(() => {
    document.body.classList.toggle(TOUR_ACTIVE_BODY_CLASS, tourIsActive);
    return () => {
      document.body.classList.remove(TOUR_ACTIVE_BODY_CLASS);
    };
  }, [tourIsActive]);

  useEffect(() => {
    if (currentUser?.id && status === "idle") {
      void dispatch(fetchOnboardingState());
    }
  }, [currentUser?.id, status, dispatch]);

  useEffect(() => {
    if (welcomeTriggeredRef.current) {
      return;
    }
    if (status !== "ready" || !remote || remote.welcome.seenAt !== null || suggestionsMuted) {
      return;
    }
    if (!currentUser?.has_accepted_cgv) {
      return;
    }
    if (activeTour || showStandardModal.show) {
      return;
    }
    const expectedPath = audience === "enduser" ? "/stats" : "/";
    if (location.pathname !== expectedPath) {
      return;
    }
    welcomeTriggeredRef.current = true;
    enqueueOnboardingEvent({
      event: "welcome_shown",
      surface: "welcome",
      itemId: null,
      step: null,
    });
    dispatch(openStandardModal(ONBOARDING_WELCOME_MODAL_ID));
  }, [
    status,
    remote,
    suggestionsMuted,
    currentUser,
    activeTour,
    showStandardModal.show,
    audience,
    location.pathname,
    dispatch,
  ]);

  useEffect(() => {
    if (relaunchTriggeredRef.current) {
      return;
    }
    if (status !== "ready" || !remote) {
      return;
    }
    if (!remote.relaunchPrompt || remote.relaunchPrompt.answeredAt !== null) {
      return;
    }
    if (suggestionsMuted) {
      return;
    }
    if (remote.welcome.seenAt === null) {
      return;
    }
    if (!currentUser?.has_accepted_cgv) {
      return;
    }
    if (activeTour || showStandardModal.show) {
      return;
    }
    const expectedPath = audience === "enduser" ? "/stats" : "/";
    if (location.pathname !== expectedPath) {
      return;
    }
    relaunchTriggeredRef.current = true;
    enqueueOnboardingEvent({
      event: "relaunch_shown",
      surface: "welcome",
      itemId: null,
      step: null,
    });
    dispatch(openStandardModal(ONBOARDING_RELAUNCH_MODAL_ID));
  }, [
    status,
    remote,
    suggestionsMuted,
    currentUser,
    activeTour,
    showStandardModal.show,
    audience,
    location.pathname,
    dispatch,
  ]);

  return (
    <>
      {tour && step && targetEl && !hiddenByBlocker && (
        <>
          {!referenceHidden && !(step.hint && focusWithinTarget) && (
            <SpotlightOverlay target={targetEl} variant={step.hint ? "ring" : "dim"} />
          )}
          <TourPopover
            key={step.anchor}
            target={targetEl}
            tour={tour}
            step={step}
            stepIndex={stepIndex}
            onNext={next}
            onBack={back}
            onSkip={handleDismiss}
            onHandoff={handoff}
            onReferenceHiddenChange={handleReferenceHiddenChange}
            positionOffset={activeTour?.positionOffset}
            hideCounter={activeTour?.rejoined}
            skippedSteps={activeTour?.skippedSteps}
          />
        </>
      )}
      <ChecklistLauncher resumable={resumable} />
      <WelcomeModal />
      <RelaunchPrompt />
      <DemoCleanupPrompt />
    </>
  );
};

export default TourLayer;
